import { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

const EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
const WARN_MIN = 5;

// Zacht belletje (twee tonen) via WebAudio — geen bestand nodig
function chime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [[660, 0], [880, 0.18]].forEach(([f, at]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.6);
      o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + 0.7);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch { /* geluid is niet blokkerend */ }
}

// Na X minuten zonder beweging (per gebruiker, minimum 1 uur): 5 min vooraf een waarschuwing met geluid, daarna afmelden → logboek "automatisch afgemeld"
export default function IdleGuard() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const timers = useRef({});
  const [deadline, setDeadline] = useState(null);
  const [left, setLeft] = useState(0);

  const arm = useCallback(() => {
    const minutes = Math.max(60, Number(user?.idle_minutes) || 60);
    clearTimeout(timers.current.warn); clearTimeout(timers.current.out);
    setDeadline(null);
    timers.current.warn = setTimeout(() => { setDeadline(Date.now() + WARN_MIN * 60 * 1000); chime(); }, (minutes - WARN_MIN) * 60 * 1000);
    timers.current.out = setTimeout(() => logout("idle"), minutes * 60 * 1000);
  }, [user?.idle_minutes, logout]);

  useEffect(() => {
    if (!user) return;
    arm();
    // tijdens de waarschuwing telt enkel de knop (of een klik erop) als "blijven": bewegen alleen is niet genoeg
    const onActivity = () => { if (!timers.current.warning) arm(); };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => { clearTimeout(timers.current.warn); clearTimeout(timers.current.out); EVENTS.forEach((e) => window.removeEventListener(e, onActivity)); };
  }, [user, arm]);

  useEffect(() => {
    timers.current.warning = !!deadline;
    if (!deadline) return;
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (!user || !deadline) return null;
  const mm = String(Math.floor(left / 60)).padStart(2, "0"), ss = String(left % 60).padStart(2, "0");
  return (
    <div className="fixed bottom-5 right-5 z-[90] w-80 rounded-2xl border border-amber-300 bg-card p-4 shadow-2xl animate-in slide-in-from-bottom-4" role="alertdialog" data-testid="idle-warning">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Clock className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">{t("idle_warn_title")}</p>
          <p className="text-xs text-muted-foreground">{t("idle_warn_text")} <span className="font-mono font-bold text-amber-700" data-testid="idle-countdown">{mm}:{ss}</span></p>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button className="flex-1" onClick={arm} data-testid="idle-stay">{t("idle_stay")}</Button>
        <Button variant="outline" onClick={() => logout("logout")} data-testid="idle-logout">{t("logout")}</Button>
      </div>
    </div>
  );
}
